import { useUserDataStore } from "@proj-client/stores/userDataStore"
import { countries } from "@proj-common/pricing/paddleCountries"
import type { PaddleGetCustomerDetailsResult } from "@proj-common/types"
import axios, { AxiosError } from "axios"
import { useEffect, useState } from "react"
import { FieldValues, useForm } from 'react-hook-form'
import { GenericModal } from "../modals/genericModal"
import { ComparePlans } from "../generic/plans"
import moment from "moment"
import Cookies from 'js-cookie'

interface IUserSubscription {
    subscriptionModalOpen: boolean
    closeSubscriptionModal: () => void
}

export const UserSubscription = (props: IUserSubscription) => {
    const {
        register,
        unregister,
        handleSubmit,
        trigger,
        getValues,
        setValue,
        formState,
        reset,
        setError,
        clearErrors,
    } = useForm()

    const userData = useUserDataStore()

    const [userDetails, setUserDetails] = useState<PaddleGetCustomerDetailsResult>(null)
    const [inputsDisabled, setInputsDisabled] = useState(userData.email == "demo@ideate.live" ? true : false)
    const [submitDisabled, setSubmitDisabled] = useState(userData.email == "demo@ideate.live" ? true : false)
    const [buttonSpinners, setButtonSpinners] = useState({
        changeSubscriptionButton: false,
        subscriptionStatusButton: false,
        updatePaymentButton: false,
        saveBillingDetailsButton: false,
    })
    const [showPlans, setShowPlans] = useState(false)
    const [regType, setRegType] = useState<"Individual" | "Business">(null)
    const [originalRegType, setOriginalRegType] = useState<"Individual" | "Business">(null)

    useEffect(() => {
        if ((regType != originalRegType && regType == 'Individual' && !formState.isValidating && formState.isValid)
            || (formState.isDirty && Object.keys(formState.dirtyFields).length > 0 && !formState.isValidating && formState.isValid)) {
            setSubmitDisabled(userData.email == "demo@ideate.live" ? true : false)
        } else {
            setSubmitDisabled(true)
        }
    }, [formState])

    useEffect(() => {
        if (userData?.subscriptionDetails.subscription) {
            axios.get("/api/payment/userDetails").then(res => {
                let data: PaddleGetCustomerDetailsResult = res.data
                setUserDetails(data)
                setRegType(data?.business ? "Business" : "Individual")
                setOriginalRegType(data?.business ? "Business" : data?.address ? "Individual" : null)
            })
        }
    }, [userData])

    useEffect(() => {
        if (regType && userDetails) {
            if (regType != null) {
                let defaultValues: FieldValues = {
                    billingName: userDetails.customer.name || "",
                    postalCode: userDetails.address?.postalCode || "",
                    countryCode: userDetails.address?.countryCode || "",
                    region: userDetails.address?.region || "",
                    city: userDetails.address?.city || "",
                    address1: userDetails.address?.firstLine || "",
                    address2: userDetails.address?.secondLine || "",
                    regType: "individual",
                }
                if (regType == "Business") {
                    defaultValues = {
                        ...defaultValues,
                        billingCompany: userDetails.business?.name || "",
                        companyNumber: userDetails.business?.companyNumber || "",
                        vatNumber: userDetails.business?.taxIdentifier || "",
                        regType: "business",
                    }
                }
                reset(defaultValues, {
                    keepDirtyValues: false,
                    keepDirty: false,
                })
            }
        }
    }, [userDetails, regType])

    useEffect(() => {
        if (regType) {
            setValue("regType", regType.toLowerCase())
            switch (regType) {
                case "Individual":
                    unregister(["billingCompany", "companyNumber", "vatNumber"])
                    break
            }
            clearErrors()
        }
    }, [regType])

    const handleNewSubscription = async (lookupKey: string) => {
        setButtonSpinners({
            ...buttonSpinners,
            changeSubscriptionButton: true
        })
        setInputsDisabled(true)

        let resp = await axios.post('/api/subscription/new', {
            lookupKey,
        })

        if (resp && resp.data && resp.data.transactionId) {
            // store current page in cookie for redirect later
            Cookies.set("paddle-checkout-redirect", window.location.pathname)
            window.location.pathname = `/checkout/${resp.data.transactionId}`
        }

        setInputsDisabled(false)
        setButtonSpinners({
            ...buttonSpinners,
            changeSubscriptionButton: false
        })
    }

    const handleChangeSubscription = async (lookupKey: string) => {
        setButtonSpinners({
            ...buttonSpinners,
            changeSubscriptionButton: true
        })
        setInputsDisabled(true)

        let resp = await axios.post('/api/subscription/change', {
            lookupKey,
        })

        if (resp && resp.data && resp.data.subscriptionDetails) {
            userData.setSubscriptionDetails(resp.data.subscriptionDetails)
        }

        setInputsDisabled(false)
        setButtonSpinners({
            ...buttonSpinners,
            changeSubscriptionButton: false
        })
    }

    const handleBillingChange = async (data: FieldValues) => {
        try {
            if (formState.isDirty && Object.keys(formState.dirtyFields).length > 0 || originalRegType != regType) {
                setButtonSpinners({
                    ...buttonSpinners,
                    saveBillingDetailsButton: true
                })
                setInputsDisabled(true)
                let resp = await axios.post("/api/payment/userDetails", {
                    ...data,
                    dirtyFields: formState.dirtyFields
                })
                setInputsDisabled(false)
                setButtonSpinners({
                    ...buttonSpinners,
                    saveBillingDetailsButton: false
                })
                if (resp && resp.data && resp.data.subscriptionDetails) {
                    userData.setSubscriptionDetails(resp.data.subscriptionDetails)
                }
                if (resp && resp.data) {
                    delete resp.data.subscriptionDetails
                    setUserDetails(resp.data)
                }
            }
        } catch (e) {
            console.log(e)
            setInputsDisabled(userData.email == "demo@ideate.live" ? true : false)
            let err = e as AxiosError<{ result: string }>
            if (err?.response?.data?.result) {
                console.log(err.response.data.result)
                // switch (err.response.data.result) {
                //     case REG_ERRORS.vat_invalid:
                //         setError("vatNumber", { type: "typeMismatch" })
                //         break
                // }
            }
        }
    }

    const handleChangePaymentMethod = async () => {
        setButtonSpinners({
            ...buttonSpinners,
            updatePaymentButton: true
        })
        setInputsDisabled(true)
        let resp = await axios.get('/api/payment/methodChange')
        if (resp && resp.data && resp.data.transactionId) {
            // store current page in cookie for redirect later
            Cookies.set("paddle-checkout-redirect", window.location.pathname)
            window.location.pathname = `/checkout/${resp.data.transactionId}`
        }
        //setInputsDisabled(false)
        // setButtonSpinners({
        //     ...buttonSpinners,
        //     updatePaymentButton: false
        // })
    }

    const handleCancelSubscription = async () => {
        setButtonSpinners({
            ...buttonSpinners,
            subscriptionStatusButton: true
        })
        setInputsDisabled(true)
        let resp = await axios.get('/api/subscription/cancel')
        if (resp && resp.data && resp.data.subscriptionDetails) {
            userData.setSubscriptionDetails(resp.data.subscriptionDetails)
        }
        setInputsDisabled(false)
        setButtonSpinners({
            ...buttonSpinners,
            subscriptionStatusButton: false
        })
    }
    const handleResumeSubscription = async () => {
        setButtonSpinners({
            ...buttonSpinners,
            subscriptionStatusButton: true
        })
        setInputsDisabled(true)
        let resp = await axios.get('/api/subscription/resume')
        if (resp && resp.data && resp.data.subscriptionDetails) {
            userData.setSubscriptionDetails(resp.data.subscriptionDetails)
        }
        setInputsDisabled(false)
        setButtonSpinners({
            ...buttonSpinners,
            subscriptionStatusButton: false
        })
    }

    return props.subscriptionModalOpen && (
        <>
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                    <div className="fixed inset-0 transition-opacity">
                        <div className="absolute inset-0 bg-gray-500 opacity-75"
                            onClick={() => props.closeSubscriptionModal()}></div>
                    </div>
                    <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg w-full" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
                        <div className="px-4 pt-5 bg-white sm:px-6">
                            <div className="sm:flex sm:items-start sm:w-full">
                                <div className="text-left flex-1">
                                    <h3 className="text-lg font-medium leading-6 text-gray-900 font-display pb-1" id="modal-headline">
                                        Manage Subscription
                                    </h3>
                                    <div className="mt-2">
                                        <div className="text-sm leading-5 text-gray-500">
                                            <div className='-mx-4 sm:-mx-6 border-b border-black border-opacity-10 h-[1px] mb-2' />
                                            <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                <div className="text-gray-800 sm:col-span-2 text-base">Subscription details</div>
                                            </div>
                                            <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2'>
                                                <label className="font-semibold text-gray-800">Subscription</label>
                                                <div className="flex place-content-between items-center">
                                                    <div>{userData.subscriptionDetails.subscription && userData.subscriptionDetails.subscription.status != "canceled" ? `${userData.subscriptionDetails.product.paddleProductData.name} ${userData.subscriptionDetails.subscription.billingCycle.interval}ly` : "No subscription"}</div>
                                                    <div className="sm:text-right">
                                                        {userData.subscriptionDetails.subscription && userData.subscriptionDetails.subscription.status != "canceled" &&
                                                            <button disabled={inputsDisabled} className="idtn-btn-secondary-sm w-[4.5rem]" onClick={() => setShowPlans(true)}>
                                                                {buttonSpinners.changeSubscriptionButton ? <i className='fa-solid fa-circle-notch fa-spin' /> : "Change"}
                                                            </button>}
                                                        {(userData.subscriptionDetails.subscription == null || userData.subscriptionDetails.subscription.status == "canceled") &&
                                                            <button disabled={inputsDisabled} className="idtn-btn-secondary-sm w-[4.5rem]" onClick={() => setShowPlans(true)}>
                                                                {buttonSpinners.changeSubscriptionButton ? <i className='fa-solid fa-circle-notch fa-spin' /> : "Select"}
                                                            </button>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2'>
                                                <label className="font-semibold text-gray-800">Status</label>
                                                <div className="flex place-content-between items-center">
                                                    {userData.subscriptionDetails.subscription?.scheduledChange?.action == "cancel" && <>
                                                        <div>Pending cancellation</div>
                                                        <div className="sm:text-right">
                                                            <button disabled={inputsDisabled} className="idtn-btn-secondary-sm w-[4.5rem]" onClick={handleResumeSubscription}>
                                                                {buttonSpinners.subscriptionStatusButton ? <i className='fa-solid fa-circle-notch fa-spin' /> : "Resume"}
                                                            </button>
                                                        </div>
                                                    </>}
                                                    {userData.subscriptionDetails.subscription?.scheduledChange == null && (userData.subscriptionDetails.subscription?.status == "active" || userData.subscriptionDetails.subscription?.status == "trialing") && <>
                                                        <div>{userData.subscriptionDetails.subscription.status}</div>
                                                        <div className="sm:text-right">
                                                            <button disabled={inputsDisabled} className="idtn-btn-secondary-sm w-[4.5rem]" onClick={handleCancelSubscription}>
                                                                {buttonSpinners.subscriptionStatusButton ? <i className='fa-solid fa-circle-notch fa-spin' /> : "Cancel"}
                                                            </button>
                                                        </div>
                                                    </>}
                                                    {userData.subscriptionDetails.subscription?.scheduledChange == null && userData.subscriptionDetails.subscription?.status == "canceled" && <>
                                                        <div>Expired</div>
                                                    </>}
                                                </div>
                                            </div>

                                            <div className='-mx-4 sm:-mx-6 border-b border-black border-opacity-10 h-[1px] mb-2' />

                                            {userData.subscriptionDetails.subscription?.nextTransaction && <>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                    <div className="text-gray-800 sm:col-span-2 text-base">Payment details</div>
                                                </div>

                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2'>
                                                    <label className="font-semibold text-gray-800">Next payment at</label>
                                                    <div>{userData.subscriptionDetails.subscription ? moment(userData.subscriptionDetails.subscription.nextBilledAt).calendar() : ""}</div>
                                                </div>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2'>
                                                    <label className="font-semibold text-gray-800">Amount charged</label>
                                                    <div className="flex place-content-between items-center">
                                                        <div>${parseInt(userData.subscriptionDetails.subscription.nextTransaction.details.totals.grandTotal) / 100}</div>
                                                        <div className="sm:text-right">
                                                            <button disabled={inputsDisabled} className="idtn-btn-secondary-sm" onClick={handleChangePaymentMethod}>
                                                                {buttonSpinners.updatePaymentButton ? <i className='fa-solid fa-circle-notch fa-spin' /> : "Update payment"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {userDetails.creditBalance.length > 0 && userDetails.creditBalance?.get(0)?.balance.available && <>
                                                    <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2'>
                                                        <label className="font-semibold text-gray-800">Available balance</label>
                                                        <div>${parseInt(userDetails.creditBalance.get(0).balance.available) / 100}</div>
                                                    </div>
                                                </>}

                                                <div className='-mx-4 sm:-mx-6 border-b border-black border-opacity-10 h-[1px] mb-2' />
                                            </>}

                                            {userData.subscriptionDetails.subscription != null && userData.subscriptionDetails.subscription.nextTransaction == null && userData.subscriptionDetails.subscription.scheduledChange && userData.subscriptionDetails.subscription.scheduledChange.action == "cancel" && <>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                    <div className="text-gray-800 sm:col-span-2 text-base">Payment details</div>
                                                </div>

                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2'>
                                                    <label className="font-semibold text-gray-800">Cancellation at</label>
                                                    <div>{moment(userData.subscriptionDetails.subscription.scheduledChange.effectiveAt).calendar()}</div>
                                                </div>

                                                <div className='-mx-4 sm:-mx-6 border-b border-black border-opacity-10 h-[1px] mb-2' />
                                            </>}

                                            <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                <div className="text-gray-800 sm:col-span-2 text-base">Billing details</div>
                                            </div>
                                            <form onSubmit={handleSubmit((data) => handleBillingChange(data), (invalid) => console.log("invalid", invalid))}>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2'>
                                                    <label className="font-semibold text-gray-800">Billing type</label>
                                                    <div className="flex place-content-between items-center">
                                                        <div>{regType}</div>
                                                        <div className="sm:text-right">
                                                            <button type="button" disabled={inputsDisabled} className="idtn-btn-secondary-sm" onClick={() => {
                                                                regType == "Business" ? setRegType("Individual") : setRegType("Business")
                                                            }}>
                                                                Change
                                                            </button>
                                                            <input type="hidden" {...register("regType", { required: true })} />
                                                        </div>
                                                    </div>
                                                </div>
                                                {
                                                    regType == "Business" && <>
                                                        <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                            <label className="font-semibold text-gray-800">Company name</label>
                                                            <input className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm"
                                                                disabled={inputsDisabled}
                                                                {...register("billingCompany", { required: true })} />
                                                            {formState.errors?.billingCompany?.type == "required" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Missing company name</p>}
                                                        </div>
                                                        <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                            <label className="font-semibold text-gray-800">Company Number <small className="font-light italic">Either</small></label>
                                                            <input className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm"
                                                                disabled={inputsDisabled}
                                                                {...register("companyNumber", {
                                                                    validate: {
                                                                        required: (value: string, formValues) => getValues("vatNumber")?.trim()?.length > 0 || value?.trim()?.length > 0
                                                                    }
                                                                })} />
                                                            {formState.errors?.companyNumber?.type == "required" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Missing company number</p>}
                                                        </div>
                                                        <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                            <label className="font-semibold text-gray-800">Company VAT Number <small className="font-light italic">Either</small></label>
                                                            <input className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm"
                                                                disabled={inputsDisabled}
                                                                {...register("vatNumber", {
                                                                    validate: {
                                                                        required: (value: string, formValues) => getValues("companyNumber")?.trim()?.length > 0 || value?.trim()?.length > 0
                                                                    }
                                                                })} />
                                                            {formState.errors?.vatNumber?.type == "required" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Missing company VAT number</p>}
                                                            {formState.errors?.vatNumber?.type == "typeMismatch" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Invalid company VAT number</p>}
                                                        </div>
                                                    </>
                                                }

                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                    <label className="font-semibold text-gray-800">Your name</label>
                                                    <input className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm"
                                                        disabled={inputsDisabled}
                                                        {...register("billingName", { required: true })} />
                                                    {formState.errors?.billingName?.type == "required" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Missing billing name</p>}
                                                </div>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                    <label className="font-semibold text-gray-800">Postal code</label>
                                                    <input className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm"
                                                        disabled={inputsDisabled}
                                                        {...register("postalCode", { required: true })} />
                                                    {formState.errors?.postalCode?.type == "required" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Missing postal code</p>}
                                                </div>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                    <label className="font-semibold text-gray-800">Country</label>
                                                    <select {...register('countryCode', {
                                                        validate: {
                                                            required: (value: string, formValues) => value?.trim()?.length > 0,
                                                            valueMismatch: (value: string, formValues) => Object.keys(countries).includes(value)
                                                        }
                                                    })
                                                    } className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm">
                                                        <option value="">Your country</option>
                                                        {Object.keys(countries).map(countryCode => <option key={countryCode} value={countryCode}>{countries[countryCode]}</option>)}
                                                    </select>
                                                    {formState.errors?.countryCode?.type == "required" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Missing country</p>}
                                                    {formState.errors?.countryCode?.type == "valueMismatch" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Wrong country</p>}
                                                </div>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                    <label className="font-semibold text-gray-800">Region <small className="font-light italic">Optional</small></label>
                                                    <input className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm"
                                                        disabled={inputsDisabled}
                                                        {...register("region")} />
                                                </div>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                    <label className="font-semibold text-gray-800">City</label>
                                                    <input className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm"
                                                        disabled={inputsDisabled}
                                                        {...register("city", { required: true })} />
                                                    {formState.errors?.city?.type == "required" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Missing city</p>}
                                                </div>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                    <label className="font-semibold text-gray-800">Address line 1</label>
                                                    <input className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm"
                                                        disabled={inputsDisabled}
                                                        {...register("address1", { required: true })} />
                                                    {formState.errors?.address1?.type == "required" && <p className="mt-1 sm:col-start-2 text-rose-500 text-xs">Missing address</p>}
                                                </div>
                                                <div className='grid items-baseline grid-cols-1 mb-2 gap-x-2 sm:grid-cols-2 '>
                                                    <label className="font-semibold text-gray-800">Address line 2 <small className="font-light italic">Optional</small></label>
                                                    <input className="px-2 py-1 w-full inline-flex items-center justify-center rounded-md border-gray-300 text-gray-600 text-sm"
                                                        disabled={inputsDisabled}
                                                        {...register("address2")} />
                                                </div>

                                                <div className='grid items-baseline grid-cols-2 sm:grid-cols-2 mb-2 gap-x-2'>
                                                    <button className="idtn-btn-primary-md col-start-2 sm:col-start-3" disabled={inputsDisabled || submitDisabled} type="submit">
                                                        <span className={inputsDisabled ? '' : 'hidden'}><i className='fa-solid fa-circle-notch fa-spin mr-2' /></span>
                                                        Save Changes
                                                    </button>
                                                </div>

                                            </form>

                                            <div className='-mx-4 sm:-mx-6 border-b border-black border-opacity-10 h-[1px]' />

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 px-4 py-3 gap-x-2 sm:px-6">
                            <button
                                type="button"
                                className="inline-flex justify-center w-full col-start-2 px-4 py-2 mr-2 text-base font-medium leading-6 text-indigo-600 transition duration-150 ease-in-out border border-indigo-600 rounded-md shadow-sm hover:border-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo sm:text-sm sm:leading-5"
                                onClick={() => {
                                    props.closeSubscriptionModal()
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div >

            {showPlans &&
                <GenericModal
                    modalTitle="Choose a plan"
                    isScrollingEnabled={true}
                    maxWidth="max-w-6xl"
                    onClose={() => setShowPlans(false)}
                    children={
                        <>
                            <ComparePlans hideTrialText={true} overrideButtonTexts={"Select"} selectedPlanCB={async (priceLookupKey) => {
                                setShowPlans(false)
                                if (userData.subscriptionDetails.subscription != null && userData.subscriptionDetails.subscription.status != "canceled") {
                                    await handleChangeSubscription(priceLookupKey)
                                } else {
                                    await handleNewSubscription(priceLookupKey)
                                }
                            }} />
                        </>
                    }
                />}
        </>
    )
}